import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getCameraActivityMeasurement,
  type CameraActivityMeasurementResponse,
  type CameraActivityMeasurementResult,
  type CameraActivityScope,
} from '../services/cameraActivityMeasurementApi';
import {
  cameraMeasurementWindowForPreset,
  resolveCameraMeasurementIdentity,
  type CameraMeasurementWindowPreset,
} from '../domain/cameraMeasurement/cameraMeasurementPresentation';

export type CameraMeasurementLoadState =
  | { status: 'idle' }
  | { status: 'identity_unavailable'; reason: 'missing_identity' | 'device_unresolved' }
  | { status: 'loading' }
  | { status: 'ok'; data: CameraActivityMeasurementResponse }
  | { status: 'http'; httpStatus: number; error: string | null; detail: string | null }
  | { status: 'network'; message: string };

type UseCameraActivityMeasurementArgs = {
  siteId?: string | null;
  deviceId?: string | null;
  cameraId?: string | null;
  inventoryDeviceId?: string | null;
  scope: CameraActivityScope;
  zoneId?: string | null;
  zoneVersion?: number | null;
  preset?: CameraMeasurementWindowPreset;
  windowStart?: string | null;
  windowEnd?: string | null;
  now?: Date;
  override?: CameraActivityMeasurementResponse | null;
  enabled?: boolean;
};

export function useCameraActivityMeasurement({
  siteId,
  deviceId,
  cameraId,
  inventoryDeviceId,
  scope,
  zoneId,
  zoneVersion,
  preset = 'last_complete_hour',
  windowStart,
  windowEnd,
  now,
  override = null,
  enabled = true,
}: UseCameraActivityMeasurementArgs): CameraMeasurementLoadState {
  const identity = useMemo(
    () => resolveCameraMeasurementIdentity({
      siteId,
      cameraId,
      deviceId,
      inventoryDeviceId,
    }),
    [siteId, cameraId, deviceId, inventoryDeviceId],
  );
  const window = useMemo(() => {
    const start = String(windowStart || '').trim();
    const end = String(windowEnd || '').trim();
    if (start && end) return { start, end };
    return cameraMeasurementWindowForPreset(preset, now || new Date());
  }, [windowStart, windowEnd, preset, now]);
  const [state, setState] = useState<CameraMeasurementLoadState>({ status: 'idle' });
  const generationRef = useRef(0);

  useEffect(() => {
    if (override) {
      setState({ status: 'ok', data: override });
      return undefined;
    }
    if (!enabled) {
      setState({ status: 'idle' });
      return undefined;
    }
    if (!identity.ok) {
      setState({ status: 'identity_unavailable', reason: identity.reason });
      return undefined;
    }
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    setState({ status: 'loading' });
    let cancelled = false;
    void getCameraActivityMeasurement({
      siteId: identity.siteId,
      deviceId: identity.deviceId,
      cameraId: identity.cameraId,
      start: window.start,
      end: window.end,
      scope,
      zoneId: scope === 'ZONE' ? zoneId : null,
      zoneVersion: scope === 'ZONE' ? zoneVersion : null,
    }).then((result: CameraActivityMeasurementResult) => {
      if (cancelled || generation !== generationRef.current) return;
      if (result.kind === 'ok') {
        setState({ status: 'ok', data: result.data });
        return;
      }
      if (result.kind === 'http') {
        setState({
          status: 'http',
          httpStatus: result.status,
          error: result.error,
          detail: result.detail,
        });
        return;
      }
      setState({ status: 'network', message: result.message });
    });
    return () => {
      cancelled = true;
    };
  }, [
    override,
    enabled,
    identity,
    window.start,
    window.end,
    scope,
    zoneId,
    zoneVersion,
  ]);

  return state;
}
